import { useMemo } from "react";
import type { Location } from "react-router-dom";
import type {
  AuthOnFile,
  JCodeDetails,
} from "../../../data-model/medicalPaOrdersModel";
import { getJCodesWithAuthLetterDocPath } from "../../../utils/getJCodesWithAuthLetterDocPath";
import type { ApprovalStatus } from "../utils/constants";

export interface AuthorisationDetailsLocationState {
  letterDocPath?: string;
  letterTitle?: string;
  jCodeId?: string;
  agentRerunLoading?: boolean;
  jCode?: JCodeDetails;
}

export interface ApprovalState {
  basicInfoStatus: ApprovalStatus;
  diagnosisDetailStatus: ApprovalStatus;
  procedureStatuses: Record<string, ApprovalStatus>;
}

/**
 * Tracks when a section’s reject state was loaded from Postgres `*_validated`
 * flags (vs the user clicking Reject). Used to suppress “feedback mode” UI
 * until the user changes approval on that section.
 */
export interface ServerHydratedRejectFlags {
  basic: boolean;
  diagnosis: boolean;
  procedures: Record<string, boolean>;
}

export interface UseAuthorisationLetterContextResult {
  letterDocPath: string | undefined;
  letterTitle: string;
  jCodes: JCodeDetails[];
  jCode: JCodeDetails | undefined;
  allPartsHaveStatus: boolean | undefined;
}

/**
 * Derives letter/JCode context from location.state and auth-on-file data so
 * the page can focus on layout and handlers. Optionally accepts approval state
 * to compute allPartsHaveStatus.
 */
export function useAuthorisationLetterContext(
  location: Location,
  authOnFile: AuthOnFile | null | undefined,
  approvalState?: ApprovalState,
  basicInfoHasChanges?: boolean,
  diagnosisDetailHasChanges?: boolean,
  procedureChangesMap?: Record<string, boolean>,
  serverHydratedReject: ServerHydratedRejectFlags = {
    basic: false,
    diagnosis: false,
    procedures: {},
  },
): UseAuthorisationLetterContextResult {
  const state = location.state as AuthorisationDetailsLocationState | null;
  const letterDocPath = state?.letterDocPath;

  const jCodes = useMemo(() => {
    const refreshedJCode = (authOnFile?.entries ?? authOnFile?.auth_entries)
      ?.flatMap((entry) => entry.j_codes ?? [])
      .find(
        (candidate) =>
          candidate.id === state?.jCodeId ||
          candidate.id === state?.jCode?.id ||
          candidate.j_code === state?.jCode?.j_code,
      );
    if (refreshedJCode) return [refreshedJCode];

    const jCodesFromLetter = getJCodesWithAuthLetterDocPath(
      authOnFile,
      letterDocPath,
    );
    if (jCodesFromLetter.length > 0) return jCodesFromLetter;
    if (state?.jCode) return [state.jCode];
    return [];
  }, [authOnFile, letterDocPath, state?.jCode, state?.jCodeId]);

  const jCode = jCodes[0] ?? state?.jCode;

  const letterTitle =
    state?.letterTitle ??
    (jCode?.auth_letter?.auth_letter_auth_number
      ? `Auth ${jCode.auth_letter.auth_letter_auth_number}.pdf`
      : "Authorization Letter.pdf");

  const checkIfBasicInfoHasChanges = useMemo(() => {
    if (approvalState?.basicInfoStatus === "valid") return true;
    if (approvalState?.basicInfoStatus === "invalid") {
      if (serverHydratedReject.basic) return true;
      if (basicInfoHasChanges) return true;
    }
    return false;
  }, [
    approvalState?.basicInfoStatus,
    basicInfoHasChanges,
    serverHydratedReject.basic,
  ]);

  const checkIfDiagnosisDetailHasChanges = useMemo(() => {
    if (approvalState?.diagnosisDetailStatus === "valid") return true;
    if (approvalState?.diagnosisDetailStatus === "invalid") {
      if (serverHydratedReject.diagnosis) return true;
      if (diagnosisDetailHasChanges) return true;
    }
    return false;
  }, [
    approvalState?.diagnosisDetailStatus,
    diagnosisDetailHasChanges,
    serverHydratedReject.diagnosis,
  ]);

  const checkIfProcedureHasChanges = useMemo(() => {
    if (jCodes.length === 0) return false;
    const statuses = approvalState?.procedureStatuses ?? {};
    const hydrated = serverHydratedReject.procedures;
    return jCodes.every((j) => {
      const id = j.id ?? j.j_code ?? "";
      const status = statuses[id];
      if (status === "valid") return true;
      if (status === "invalid") {
        if (hydrated[id]) return true;
        return procedureChangesMap?.[id] === true;
      }
      return false;
    });
  }, [
    approvalState?.procedureStatuses,
    procedureChangesMap,
    jCodes,
    serverHydratedReject.procedures,
  ]);

  const allPartsHaveStatus = useMemo(() => {
    return (
      checkIfBasicInfoHasChanges &&
      checkIfDiagnosisDetailHasChanges &&
      checkIfProcedureHasChanges
    );
  }, [
    checkIfBasicInfoHasChanges,
    checkIfDiagnosisDetailHasChanges,
    checkIfProcedureHasChanges,
  ]);

  return {
    letterDocPath,
    letterTitle,
    jCodes,
    jCode,
    allPartsHaveStatus,
  };
}
